/**
 * Recoveries that wait for Word at the same time are recovered together, in one hold of the
 * write lock.
 *
 * A lost bookmark is recovered by reading the body and checking the card's anchors against it
 * (`RecoveryChain`). Each tracker used to take the write lock and recover for itself, so twenty
 * lost bookmarks read the body twenty times, one lock hold after another.
 *
 * The recoveries already queue for the one write lock. The first to ask for it asks on behalf of
 * everyone waiting: when the lock is granted, every recovery that asked before then is handed to
 * the batch's one handler in that one hold, which reads the document once, resolves every
 * recovery against that read, and writes everything it found together
 * (`AnchorTracker.reconcileBatch`).
 */
import type { WordDocument, WordSession } from "./word-document";

/**
 * What the batch's handler does with the requests of one hold: one outcome per request, in the
 * request's order, so one recovery's failure settles that recovery alone.
 */
export type RecoverBatch<Request, Recovered> = (session: WordSession, requests: Request[]) => Promise<PromiseSettledResult<Recovered>[]>;

/** One request queued for the next hold of the write lock, with the settling of the promise its caller holds. */
interface WaitingRequest<Request, Recovered> {
    request: Request;
    settleAsRecovered: (recovered: Recovered) => void;
    /** Settles the caller's promise as failed: the recovery failed, or the lock was never granted. */
    settleAsFailed: (error: unknown) => void;
}

export class RecoveryBatches<Request, Recovered> {
    private waitingRequests: WaitingRequest<Request, Recovered>[] = [];
    /** True from the first request's ask for the write lock until the lock is granted and its batch is taken. */
    private writeLockRequested = false;

    constructor(
        private readonly word: WordDocument,
        private readonly recoverBatch: RecoverBatch<Request, Recovered>,
    ) {}

    /**
     * Queues one request for the next hold of the write lock, together with every request that
     * asks before that hold begins.
     *
     * CASES, for a request asking now
     *   - no ask for the lock is outstanding -> it asks for the lock; its batch is whoever else asks before the grant
     *   - an ask is outstanding              -> it joins that batch and asks for nothing
     *   - a batch is running                 -> its ask waits for the lock behind that batch, as any write does,
     *                                           and its batch is the requests that ask while it waits
     *
     * EXAMPLE
     *   twenty lost comment bookmarks reconcile in the same turn -> one lock hold, one handler call, twenty requests
     *
     * RETURNS
     *   what the handler settles this request with, or its failure: one request's failure never reaches another's promise.
     */
    recoverTogether(request: Request): Promise<Recovered> {
        return new Promise<Recovered>((resolveRequest, rejectRequest) => {
            this.waitingRequests.push({ request, settleAsRecovered: resolveRequest, settleAsFailed: rejectRequest });
            this.requestTheWriteLockOnce();
        });
    }

    private requestTheWriteLockOnce(): void {
        /** WHEN an ask for the lock is already outstanding CONSEQUENCE the request just queued is part of its batch. */
        if (this.writeLockRequested) return;
        this.writeLockRequested = true;
        /**
         * WHEN this hold's callback starts
         *   CONSEQUENCE
         *     the requests waiting from then on, and the ask for the lock they made, belong to the next
         *     hold; this hold owns only the batch it took, which its handler settles.
         */
        let callbackOfThisHoldStarted = false;
        this.word
            .withWrite(async (session) => {
                /**
                 * The batch is taken when the lock is granted, not when it was asked for: every request that
                 * queued in between is in it. A request that asks from here on needs the next hold.
                 */
                callbackOfThisHoldStarted = true;
                this.writeLockRequested = false;
                const requestsOfThisBatch = this.waitingRequests;
                this.waitingRequests = [];
                await this.recoverTogetherIn(session, requestsOfThisBatch);
            })
            .catch((error: unknown) => {
                /**
                 * CASES, for a hold whose write rejected
                 *   - the callback never started (Word never granted the lock, the write could not start)
                 *       -> every request waiting fails with that error, so none waits for a batch that will not run,
                 *          and the next request to ask asks for the lock afresh.
                 *   - the callback started and Word rejected after it returned (the write's closing sync failed)
                 *       -> nothing is failed here: the batch was settled by its handler, and the requests waiting
                 *          now asked for the next hold, which serves them.
                 *
                 * EXAMPLE
                 *   R1's hold runs its handler; R2 asks meanwhile and waits for hold 2; hold 1 then rejects
                 *   -> R2 stays queued and hold 2 serves it, instead of R2 failing with hold 1's error
                 */
                /** WHEN the callback of this hold started CONSEQUENCE its batch is already settled and the requests waiting belong to the next hold, so none is failed here. */
                if (callbackOfThisHoldStarted) return;
                this.writeLockRequested = false;
                const unservedRequests = this.waitingRequests;
                this.waitingRequests = [];
                unservedRequests.forEach((unservedRequest) => unservedRequest.settleAsFailed(error));
            });
    }

    /**
     * Hands one batch to the handler and settles every request with its outcome.
     *
     * CASES
     *   - the handler answers, one outcome per request -> each request settles with its own
     *   - the handler itself fails                      -> every request of the batch fails with that error
     */
    private async recoverTogetherIn(session: WordSession, requestsOfThisBatch: WaitingRequest<Request, Recovered>[]): Promise<void> {
        try {
            const outcomes = await this.recoverBatch(session, requestsOfThisBatch.map((waitingRequest) => waitingRequest.request));
            requestsOfThisBatch.forEach((waitingRequest, requestIndex) => {
                const outcome = outcomes[requestIndex];
                if (outcome.status === "fulfilled") waitingRequest.settleAsRecovered(outcome.value);
                else waitingRequest.settleAsFailed(outcome.reason);
            });
        } catch (error) {
            requestsOfThisBatch.forEach((waitingRequest) => waitingRequest.settleAsFailed(error));
        }
    }
}
